import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api';
import type { AgencyMember, Role, Tenant } from '@/types';
import { MemberTable } from '@/components/user-management/MemberTable';
import { AddMemberDialog } from '@/components/user-management/AddMemberDialog';
import { MemberSignupStatusDialog } from '@/components/user-management/MemberSignupStatusDialog';
import { toast } from 'sonner';

interface UserManagementProps {
  role: Role;
  /** The caller's own agency (used directly for AgencyAdmin). */
  tenantId: string;
}

export function UserManagement({ role, tenantId }: UserManagementProps) {
  const isSystemAdmin = role === 'SystemAdmin';
  const queryClient = useQueryClient();

  // SystemAdmin drills in from an agency list; AgencyAdmin is pinned to their own.
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const activeTenantId = isSystemAdmin ? selectedTenant?.tenant_id : tenantId;

  const [addOpen, setAddOpen] = useState(false);
  const [editMember, setEditMember] = useState<AgencyMember | null>(null);
  const [statusMember, setStatusMember] = useState<AgencyMember | null>(null);

  // ── Agencies (SystemAdmin only) ──
  const { data: tenants = [] } = useQuery({
    queryKey: ['um-tenants'],
    queryFn: () => api.listTenants(),
    enabled: isSystemAdmin,
  });

  const [newId, setNewId] = useState('');
  const [newName, setNewName] = useState('');
  const createTenantMutation = useMutation({
    mutationFn: () => api.createTenant({ tenant_id: newId.trim(), name: newName.trim() }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['um-tenants'] });
      toast.success('הסוכנות נוצרה');
      setNewId('');
      setNewName('');
    },
    onError: (err: Error) => toast.error('שגיאה', { description: err.message }),
  });
  const toggleTenantStatus = useMutation({
    mutationFn: (t: Tenant) => api.updateTenant(t.tenant_id, { status: t.status === 'active' ? 'suspended' : 'active' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['um-tenants'] }),
    onError: (err: Error) => toast.error('שגיאה', { description: err.message }),
  });

  // ── Members ──
  const { data: members = [], isLoading: membersLoading } = useQuery({
    queryKey: ['members', activeTenantId],
    queryFn: () => api.listMembers(activeTenantId!),
    enabled: !!activeTenantId,
  });

  const invalidateMembers = () => queryClient.invalidateQueries({ queryKey: ['members', activeTenantId] });

  const suspendMutation = useMutation({
    mutationFn: (m: AgencyMember) => api.suspendMember(activeTenantId!, m.user_id),
    onSuccess: (m) => {
      invalidateMembers();
      toast.success(m.status === 'SUSPENDED' ? 'המשתמש הושעה' : 'המשתמש שוחזר');
    },
    onError: (err: Error) => toast.error('שגיאה', { description: err.message }),
  });
  const removeMutation = useMutation({
    mutationFn: (m: AgencyMember) => api.removeMember(activeTenantId!, m.user_id),
    onSuccess: () => {
      invalidateMembers();
      toast.success('המשתמש הוסר מהסוכנות');
    },
    onError: (err: Error) => toast.error('שגיאה', { description: err.message }),
  });
  const promoteMutation = useMutation({
    mutationFn: (m: AgencyMember) => api.updateMember(activeTenantId!, m.user_id, { role: 'AgencyAdmin' }),
    onSuccess: (m) => {
      invalidateMembers();
      toast.success(`${m.name || m.username} מונה למנהל סוכנות`);
    },
    onError: (err: Error) => toast.error('שגיאה', { description: err.message }),
  });

  const openAdd = () => {
    setEditMember(null);
    setAddOpen(true);
  };
  const openEdit = (m: AgencyMember) => {
    setEditMember(m);
    setAddOpen(true);
  };

  const canCreate = !!newId.trim() && !!newName.trim() && !createTenantMutation.isPending;

  // ── Agency picker (SystemAdmin, no agency selected) ──
  if (isSystemAdmin && !selectedTenant) {
    return (
      <div className="leads" dir="rtl">
        <div className="leads-header">
          <div>
            <h1 className="page-title">ניהול סוכנויות ומשתמשים</h1>
            <p className="page-subtitle">בחרו סוכנות לניהול המשתמשים שלה, או צרו סוכנות חדשה</p>
          </div>
        </div>

        <div className="leads-card" style={{ marginBottom: 16 }}>
          <div className="leads-card-header">
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <label className="lead-field" style={{ flex: 1, minWidth: 180 }}>
                <span className="lead-field-label">מזהה סוכנות (tenant_id)</span>
                <input value={newId} dir="ltr" placeholder="agency-acme" onChange={(e) => setNewId(e.target.value)} />
              </label>
              <label className="lead-field" style={{ flex: 1, minWidth: 180 }}>
                <span className="lead-field-label">שם הסוכנות</span>
                <input value={newName} placeholder="Acme Insurance" onChange={(e) => setNewName(e.target.value)} />
              </label>
              <button className="btn" disabled={!canCreate} onClick={() => createTenantMutation.mutate()}>
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14" /></svg>
                צור סוכנות
              </button>
            </div>
          </div>
        </div>

        <div className="leads-card">
          <div className="leads-card-body">
            {tenants.length === 0 ? (
              <div className="empty">אין סוכנויות עדיין — צרו את הראשונה למעלה</div>
            ) : (
              <div className="quote-table-scroll">
                <table className="crm-table">
                  <thead>
                    <tr>
                      <th>סוכנות</th>
                      <th>מזהה</th>
                      <th>סטטוס</th>
                      <th style={{ textAlign: 'center' }}>פעולות</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tenants.map((t) => (
                      <tr key={t.tenant_id}>
                        <td className="cell-primary" style={{ cursor: 'pointer' }} onClick={() => setSelectedTenant(t)}>
                          {t.name}
                        </td>
                        <td className="cell-sub" dir="ltr">{t.tenant_id}</td>
                        <td>
                          <span className={`stage-badge ${t.status === 'active' ? 'heat-closing' : 'heat-lost'}`}>
                            {t.status === 'active' ? 'פעילה' : 'מושהית'}
                          </span>
                        </td>
                        <td>
                          <div className="row-actions" style={{ justifyContent: 'center' }}>
                            <button className="btn sm" onClick={() => setSelectedTenant(t)}>
                              ניהול משתמשים
                              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
                            </button>
                            <button className="btn outline sm" onClick={() => toggleTenantStatus.mutate(t)}>
                              {t.status === 'active' ? 'השהה' : 'הפעל'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Members view ──
  return (
    <div className="leads" dir="rtl">
      <div className="leads-header">
        <div>
          {isSystemAdmin && (
            <button className="btn ghost sm" style={{ marginBottom: 8, paddingInline: 0 }} onClick={() => setSelectedTenant(null)}>
              <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
              חזרה לסוכנויות
            </button>
          )}
          <h1 className="page-title">{isSystemAdmin && selectedTenant ? selectedTenant.name : 'ניהול משתמשים'}</h1>
          <p className="page-subtitle">
            {members.length} משתמשים{activeTenantId ? ` · ${activeTenantId}` : ''}
          </p>
        </div>
        <div className="leads-actions">
          <button className="btn" onClick={openAdd} disabled={!activeTenantId}>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM19 8v6M22 11h-6" /></svg>
            הוסף משתמש
          </button>
        </div>
      </div>

      <MemberTable
        members={members}
        isLoading={membersLoading}
        canManage
        onEditMember={openEdit}
        onSuspendMember={(m) => suspendMutation.mutate(m)}
        onRemoveMember={(m) => removeMutation.mutate(m)}
        onTransferAdmin={(m) => promoteMutation.mutate(m)}
        onShowStatus={setStatusMember}
      />

      {activeTenantId && (
        <AddMemberDialog
          open={addOpen}
          onClose={() => {
            setAddOpen(false);
            setEditMember(null);
          }}
          tenantId={activeTenantId}
          editMember={editMember}
          canAssignSystemAdmin={isSystemAdmin}
        />
      )}

      {activeTenantId && (
        <MemberSignupStatusDialog
          open={!!statusMember}
          onClose={() => setStatusMember(null)}
          tenantId={activeTenantId}
          member={statusMember}
        />
      )}
    </div>
  );
}
